import { useCallback, useState, type ReactElement } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import type { Role } from '@chatapp-inbox/shared';
import { isApiError } from '../../api';
import { enabledLines, useLines } from '../conversation';
import { NewConversationDialog, templatesAvailable, useTemplates } from '../templates';
import { ConversationFilterTabs } from './ConversationFilterTabs';
import { ConversationSearch } from './ConversationSearch';
import { ConversationRow } from './ConversationRow';
import {
  EMPTY_MESSAGES,
  FILTER_PARAM,
  SEARCH_PARAM,
  defaultFilter,
  parseFilter,
  parseSearch,
  searchEmptyHint,
  searchEmptyMessage,
  searchFoundText,
} from './filters';
import { useConversations } from './use-conversations';
import { useLoadMoreSentinel } from './use-load-more-sentinel';
import { useToday } from './use-today';
import styles from './ConversationListPane.module.css';

const SKELETON_ROWS = 7;
const FALLBACK_ERROR = 'Não foi possível carregar as conversas. Tente de novo.';

const messageOf = (error: unknown): string => (isApiError(error) ? error.message : FALLBACK_ERROR);

/** Coluna esquerda da caixa de entrada: abas de filtro + lista de conversas com rolagem infinita. */
export function ConversationListPane({ role }: { role: Role }): ReactElement {
  const [searchParams] = useSearchParams();
  const { search } = useLocation();
  // Valor ausente ou inválido na URL cai no padrão do papel; a URL em si não é reescrita.
  const filter = parseFilter(searchParams.get(FILTER_PARAM)) ?? defaultFilter(role);
  // O que a pessoa procura (vem da URL, já limpo); vazio = a lista da aba.
  const query = parseSearch(searchParams.get(SEARCH_PARAM));
  const today = useToday();
  // Informação de apoio: sem resposta (ainda ou com erro) a lista segue sem o nome do número.
  const lines = useLines();
  const showLine = enabledLines(lines.data).length >= 2;
  // "Nova conversa" precisa de modelos (a Meta só deixa abrir conversa com um) e de um número ligado.
  const templates = useTemplates();
  const canStart = templatesAvailable(templates.data) && enabledLines(lines.data).length > 0;
  const [composing, setComposing] = useState(false);
  const closeComposer = useCallback(() => setComposing(false), []);

  const {
    data,
    error,
    isPending,
    isFetching,
    isPlaceholderData,
    isFetchingNextPage,
    isFetchNextPageError,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useConversations(filter, query);

  // Em estado (não em ref) para o observador ser recriado se o contêiner for trocado.
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);

  // "Tentar de novo" some assim que a nova tentativa começa (entra o esqueleto ou o botão comum
  // volta). Um botão focado que sai da tela joga o foco no <body>; por isso o foco vai antes para
  // o contêiner da lista (tabIndex -1), sem rolar, e quem usa teclado não perde o lugar.
  const keepFocusInList = useCallback(() => {
    scrollRoot?.focus({ preventScroll: true });
  }, [scrollRoot]);

  // `cancelRefetch: false`: duas interseções seguidas antes da renderização não viram duas buscas.
  const loadMoreFromScroll = useCallback(() => {
    void fetchNextPage({ cancelRefetch: false });
  }, [fetchNextPage]);

  const sentinelRef = useLoadMoreSentinel({
    root: scrollRoot,
    // Não observa durante qualquer busca (a seguinte ou uma recarga) nem depois de uma falha:
    // sem isso, um erro persistente viraria um laço de requisições.
    enabled: hasNextPage && !isFetching && !isFetchNextPageError,
    onReach: loadMoreFromScroll,
    loadedPages: data?.pages.length ?? 0,
  });

  const conversations = data?.conversations;

  let body: ReactElement;
  if (!conversations) {
    body = isPending ? (
      <ListSkeleton />
    ) : (
      <div className={styles.state}>
        <p role="alert" className={styles.stateText}>
          {messageOf(error)}
        </p>
        <button
          type="button"
          className={styles.action}
          onClick={() => {
            keepFocusInList();
            void refetch();
          }}
        >
          Tentar de novo
        </button>
      </div>
    );
  } else {
    body = (
      <>
        {query !== '' && conversations.length > 0 && !isPlaceholderData && (
          <p role="status" className="sr-only">
            {searchFoundText(conversations.length, hasNextPage)}
          </p>
        )}
        {conversations.length > 0 && (
          <ul
            className={isPlaceholderData ? `${styles.list} ${styles.stale}` : styles.list}
            aria-busy={isPlaceholderData || undefined}
          >
            {conversations.map((conversation) => (
              <ConversationRow
                key={conversation.id}
                conversation={conversation}
                search={search}
                today={today}
                showAssignee={filter === 'all' || filter === 'closed'}
                showLine={showLine}
              />
            ))}
          </ul>
        )}
        {conversations.length === 0 && !hasNextPage && (
          <div role="status" className={styles.state}>
            <p className={styles.stateText}>
              {query !== '' ? searchEmptyMessage(query) : EMPTY_MESSAGES[filter]}
            </p>
            {query !== '' && <p className={styles.hint}>{searchEmptyHint(filter)}</p>}
            {query === '' && filter === 'mine' && (
              <p className={styles.hint}>Veja a aba “Não atribuídas” para assumir uma conversa.</p>
            )}
          </div>
        )}
        {hasNextPage && (
          <div className={styles.footer}>
            <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />
            {isFetchNextPageError ? (
              <div className={styles.inlineError}>
                <p role="alert" className={styles.stateText}>
                  {messageOf(error)}
                </p>
                <button
                  type="button"
                  className={styles.action}
                  onClick={() => {
                    keepFocusInList();
                    void fetchNextPage();
                  }}
                >
                  Tentar de novo
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={styles.action}
                // aria-disabled (e não disabled): o botão focado não perde o foco ao carregar.
                aria-disabled={isFetchingNextPage || undefined}
                onClick={() => {
                  if (!isFetchingNextPage) void fetchNextPage();
                }}
              >
                {isFetchingNextPage ? 'Carregando…' : 'Carregar mais conversas'}
              </button>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <div className={styles.pane}>
      {canStart && (
        <div className={styles.toolbar}>
          <button type="button" className={styles.newButton} onClick={() => setComposing(true)}>
            Nova conversa
          </button>
        </div>
      )}
      <ConversationSearch />
      <ConversationFilterTabs active={filter} />
      {/* `key`: trocar de aba monta outro contêiner, e a lista nova começa no topo. */}
      <section
        key={filter}
        ref={setScrollRoot}
        aria-label="Lista de conversas"
        className={styles.scroll}
        // Foco só por código (não entra na ordem do Tab): ver `keepFocusInList` e a casca.
        tabIndex={-1}
        data-conversation-list=""
      >
        {body}
      </section>
      {composing && <NewConversationDialog onClose={closeComposer} />}
    </div>
  );
}

/** Esqueleto da primeira carga; o texto para leitores de tela é o que é anunciado. */
function ListSkeleton(): ReactElement {
  return (
    <div role="status">
      <span className="sr-only">Carregando conversas…</span>
      <div aria-hidden="true">
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <div key={index} className={styles.skeletonRow}>
            <span className={styles.skeletonAvatar} />
            <span className={styles.skeletonLines}>
              <span className={styles.skeletonLine} />
              <span className={`${styles.skeletonLine} ${styles.skeletonShort}`} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
